import { useQuery } from '@tanstack/react-query'
import { dealPartyService } from '@/services/dealParties'
import { RelationshipMap } from '../parties/RelationshipMap'
import '../parties/parties-intelligence.css'
import { WorkspacePanel } from './WorkspacePanel'

/** The Parties tab's relationship map, on the Overview. The map lays itself
 *  out for the panel it is given (columns chosen from the panel's aspect,
 *  scaled to fit), so every party stays in view at any size. */
export function RelationshipMapPanel({
  loanId,
  onOpenParties,
  className = '',
}: {
  loanId: string
  onOpenParties?: () => void
  className?: string
}) {
  const roster = useQuery({
    queryKey: ['deal-parties', loanId],
    queryFn: () => dealPartyService.list(loanId),
    staleTime: 30_000,
    enabled: Boolean(loanId),
  })
  const count = roster.data?.parties.length ?? 0
  // The Parties tab and /crm say this; the map here showed the lender's own
  // staff as "Other party" with nothing said (nd-077).
  const staffUnrecognised = roster.data?.identitySuppression?.staffConfigured === false
  const note = (text: string) => (
    <p className="flex flex-1 items-center justify-center px-3 py-4 text-center text-[10px] text-stone-500">
      {text}
    </p>
  )
  return (
    <WorkspacePanel
      title="Relationship Map"
      dense
      action={onOpenParties ? 'Parties' : undefined}
      onAction={onOpenParties}
      className={className}
      bodyClassName="flex flex-col !overflow-hidden !p-0"
    >
      {roster.isLoading
        ? note('Loading parties...')
        : roster.isError
          ? note('Parties could not be loaded.')
          : !roster.data || count === 0
            ? note('No parties are recorded on this deal.')
            : (
              <>
                {staffUnrecognised ? (
                  <div
                    role="note"
                    aria-label="Identity suppression not configured"
                    className="pi-note shrink-0 !px-2 !py-1 !text-[10px] !leading-snug"
                  >
                    Identity suppression is not configured, so your own staff can appear here as outside
                    parties. Set it under{' '}
                    <a href="/settings">Settings → Platform Settings → Tenant Identity Suppression</a>.
                  </div>
                ) : null}
                <RelationshipMap
                  roster={roster.data}
                  embedded
                  onSelect={() => onOpenParties?.()}
                />
              </>
            )}
    </WorkspacePanel>
  )
}